import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaUserTie, FaClipboardCheck, FaSearch, FaFilter, FaCheckCircle } from 'react-icons/fa';
import Toast from '../components/Toast';
import * as authService from '../services/authService';
import * as adminService from '../services/adminService';
import { FaGraduationCap } from 'react-icons/fa';

const ProfessorManagement = () => {
  const [professors, setProfessors] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [selectedProfessor, setSelectedProfessor] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const navigate = useNavigate();

  // Check if user is admin
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin') {
      navigate('/');
      return;
    }

    loadProfessors();
  }, [navigate]);

  const loadProfessors = async () => {
    setLoading(true);

    try {
      // Fetch data from endpoints
      const [professorsRes, editorialRes, reviewersRes] = await Promise.all([
        adminService.getProfessors(),
        adminService.getEditorialAssignments(),
        adminService.getReviewers()
      ]);

      // Normalize responses
      const allProfessors = professorsRes.data || professorsRes;
      const editorialBoard = editorialRes.data || editorialRes;
      const reviewers = reviewersRes.data || reviewersRes;

      // Create lookup sets for IDs from the specialized endpoints
      const editorialIds = new Set((Array.isArray(editorialBoard) ? editorialBoard : []).map(item => item.professor_id || item.user_id || item.id));
      const reviewerIds = new Set((Array.isArray(reviewers) ? reviewers : []).map(item => item.professor_id || item.user_id || item.id));

      // Merge data with support for nested user object and direct flags
      const professorsWithRoles = (Array.isArray(allProfessors) ? allProfessors : []).map(prof => {
        // Support nested user structure (as shown in user JSON)
        const userData = prof.user || {};

        return {
          ...prof,
          // Extract name from nested or root
          name: userData.full_name || prof.full_name || userData.name || prof.name || 'Unknown Professor',
          email: userData.email || prof.email || 'N/A',
          phone: userData.phone_number || prof.phone_number || prof.phone || 'N/A',
          id: prof.user_id || prof.id,

          // Determine roles: prioritizing direct flags is_editorial_board/is_reviewer,
          // then falling back to ID checks from the specialized endpoints
          editorialBoard: prof.is_editorial_board !== undefined ? prof.is_editorial_board : editorialIds.has(prof.user_id || prof.id),
          reviewer: prof.is_reviewer !== undefined ? prof.is_reviewer : reviewerIds.has(prof.user_id || prof.id)
        };
      });

      setProfessors(professorsWithRoles);
    } catch (error) {
      console.error('Error loading professors:', error);
      showToast('Failed to load professor data from server', 'error');
      setProfessors([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate filtered professors on-the-fly
  const getFilteredProfessors = () => {
    let filtered = [...professors];

    // Apply search filter
    if (searchTerm && searchTerm.trim() !== '') {
      const search = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(prof => {
        const name = (prof.name || '').toLowerCase();
        const email = (prof.email || '').toLowerCase();
        const phone = (prof.phone || '');

        return name.includes(search) ||
          email.includes(search) ||
          phone.includes(search);
      });
    }

    // Apply role filter and MOVE logic
    // Requirements: once assigned as reviewer or editorial, they "move" to respective management pages
    // So by default, we only show those who are NEITHER.
    filtered = filtered.filter(prof => {
      const hasSpecialRole = prof.editorialBoard === true || prof.reviewer === true;

      if (filterType === 'all' || filterType === 'none') {
        return !hasSpecialRole;
      }
      if (filterType === 'editorial') {
        return prof.editorialBoard === true;
      }
      if (filterType === 'reviewer') {
        return prof.reviewer === true;
      }
      return true;
    });

    return filtered;
  };

  const filteredProfessors = getFilteredProfessors();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  const handleRoleToggle = (professor, type) => {
    setSelectedProfessor(professor);
    setActionType(type);
    setShowConfirmModal(true);
  };

  const confirmRoleChange = async () => {
    if (!selectedProfessor) return;

    try {
      if (actionType === 'reviewer') {
        const isCurrentlyAssigned = selectedProfessor.reviewer;

        if (isCurrentlyAssigned) {
          // REMOVE using DELETE endpoint
          await adminService.removeFromReviewers(selectedProfessor.id);
          showToast(`${selectedProfessor.name} removed from Reviewers`, 'success');
        } else {
          // ADD using POST endpoint
          await adminService.assignAsReviewer({
            professor_id: selectedProfessor.id
          });
          showToast(`${selectedProfessor.name} successfully assigned as Reviewer and moved to Reviewer Management`, 'success');
        }
      } else if (actionType === 'editorial') {
        const isCurrentlyAssigned = selectedProfessor.editorialBoard;

        if (isCurrentlyAssigned) {
          await adminService.removeFromEditorialBoard(selectedProfessor.id);
          showToast(`${selectedProfessor.name} removed from Editorial Board`, 'success');
        } else {
          await adminService.assignToEditorialBoard({
            professor_id: selectedProfessor.id
          });
          showToast(`${selectedProfessor.name} successfully assigned to Editorial Board and moved to Editorial Management`, 'success');
        }
      }

      // Reload data to reflect changes
      await loadProfessors();

    } catch (error) {
      console.error('Error updating role:', error);
      showToast(error.message || 'Error updating professor role', 'error');
    } finally {
      setShowConfirmModal(false);
      setSelectedProfessor(null);
      setActionType('');
    }
  };

  const getRoleStatus = (professor) => {
    const roles = [];
    if (professor.editorialBoard === true) roles.push('Editorial Board');
    if (professor.reviewer === true) roles.push('Reviewer');
    return roles.length > 0 ? roles.join(' & ') : 'Professor';
  };

  const getRoleBadgeColor = (professor) => {
    if (professor.reviewer) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-600 border-gray-300';
  };

  const countNoRole = professors.filter(p => p.reviewer === false && p.editorialBoard === false).length;
  const countAssigned = professors.filter(p => p.reviewer === true || p.editorialBoard === true).length;

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading professors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-8 px-4">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && selectedProfessor && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {actionType === 'editorial' ? (
                  <FaUserTie className="w-8 h-8 text-blue-600" />
                ) : (
                  <FaClipboardCheck className="w-8 h-8 text-green-600" />
                )}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirm Role Change</h3>
              <p className="text-gray-600">
                {actionType === 'editorial'
                  ? (selectedProfessor.editorialBoard
                    ? `Remove ${selectedProfessor.name} from Editorial Board role?`
                    : `Add ${selectedProfessor.name} to Editorial Board?`)
                  : (selectedProfessor.reviewer
                    ? `Remove ${selectedProfessor.name} from Reviewer role?`
                    : `Assign ${selectedProfessor.name} as Reviewer?`)
                }
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedProfessor(null);
                  setActionType('');
                }}
                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmRoleChange}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w mx-auto">
        {/* Header */}
        <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FaUsers className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Professor Management</h1>
                <p className="text-blue-100 mt-1">Manage editorial board and reviewer assignments</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-blue-200">Total Professors</p>
              <p className="text-3xl font-bold text-white">{professors.length}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600 font-medium">Available Professors</p>
                  <p className="text-2xl font-bold text-blue-900 mt-1">{countNoRole}</p>
                </div>
                <FaUsers className="w-8 h-8 text-blue-500" />
              </div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-600 font-medium">Specially Assigned</p>
                  <p className="text-2xl font-bold text-green-900 mt-1">{countAssigned}</p>
                </div>
                <FaCheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-indigo-600 font-medium">Total Registered</p>
                  <p className="text-2xl font-bold text-indigo-900 mt-1">{professors.length}</p>
                </div>
                <FaGraduationCap className="w-8 h-8 text-indigo-500" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              />
            </div>

            {/* Filter */}
            <div className="relative">
              <FaFilter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent appearance-none cursor-pointer bg-white"
              >
                <option value="all">Active Professors (Available)</option>
                <option value="reviewer">Reviewers (Moved)</option>
                <option value="editorial">Editorial Board (Moved)</option>
              </select>
            </div>
          </div>

          {/* Active Filters Display */}
          {(searchTerm || filterType !== 'all') && (
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <span className="text-sm text-gray-600">Active filters:</span>
              {searchTerm && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2">
                  Search: "{searchTerm}"
                  <button onClick={() => setSearchTerm('')} className="hover:text-blue-900 font-bold">×</button>
                </span>
              )}
              {filterType !== 'all' && (
                <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm flex items-center gap-2">
                  Role: {filterType === 'editorial' ? 'Editorial Board' : filterType === 'reviewer' ? 'Reviewers' : 'No Role'}
                  <button onClick={() => setFilterType('all')} className="hover:text-blue-900 font-bold">×</button>
                </span>
              )}
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>

        {/* Professors List */}
        {filteredProfessors.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FaUsers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Professors Found</h3>
            <p className="text-gray-600">
              {searchTerm || filterType !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'No professors are registered in the system yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-2">
              Showing {filteredProfessors.length} of {professors.length} professors
            </div>
            {filteredProfessors.map((professor, index) => (
              <div
                key={`${professor.email}-${index}`}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Professor Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0" style={darkGradient}>
                          <span className="text-2xl font-bold text-white">
                            {professor.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">{professor.name}</h3>
                          <p className="text-gray-600 text-sm mb-2">{professor.email}</p>
                          {professor.phone && (
                            <p className="text-gray-500 text-sm">📞 {professor.phone}</p>
                          )}
                          <div className="mt-3">
                            <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold border-2 ${getRoleBadgeColor(professor)}`}>
                              {getRoleStatus(professor)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 lg:flex-col lg:w-64">
                      <button
                        onClick={() => handleRoleToggle(professor, 'editorial')}
                        className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${professor.editorialBoard
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg'
                          : 'bg-blue-50 text-blue-600 border-2 border-blue-200 hover:bg-blue-100'
                          }`}
                      >
                        {professor.editorialBoard && <FaCheckCircle className="w-4 h-4" />}
                        <FaUserTie className="w-4 h-4" />
                        <span>{professor.editorialBoard ? 'Editorial Board' : 'Add to Editorial'}</span>
                      </button>

                      <button
                        onClick={() => handleRoleToggle(professor, 'reviewer')}
                        className={`flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${professor.reviewer
                          ? 'bg-green-600 text-white hover:bg-green-700 shadow-lg'
                          : 'bg-green-50 text-green-600 border-2 border-green-200 hover:bg-green-100'
                          }`}
                      >
                        {professor.reviewer && <FaCheckCircle className="w-4 h-4" />}
                        <FaClipboardCheck className="w-4 h-4" />
                        <span>{professor.reviewer ? 'Reviewer' : 'Add as Reviewer'}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessorManagement;